import styled from 'styled-components'
import React, { useEffect } from 'react'
import { useIntl } from 'react-intl'
import { useState } from 'react'
import { DropdownSelect } from 'app/common/dropdown'
import { Input, Error } from 'app/common/forms'
import { Info } from 'app/assets/icons'
import { Spacer, colors } from 'app-v2/design-systems'
import { COLOR, ORIENTATION, SIZE, BORDER, SPACING } from 'app-v2/design-tokens'
import { useStudentProfileChoices } from '../useStudentProfileChoicesQuery'
import { ButtonMedium } from 'app/assets/styles'
import { colors as oldColors } from 'shared/assets/styles'
import { useSelector } from 'react-redux'
import { Text } from 'app/common/typography'
import { SmallText } from 'app/common/typography'

type StudentFormProps = {
    formik: any,
    isProfileAlreadyValidated?: boolean
}

export const StudentForm = ({
    formik,
    isProfileAlreadyValidated
}: StudentFormProps) => {
    const { values, setFieldValue, errors, touched, isSubmitting } = formik
    const intl = useIntl()
    const { profileUpdateError } = useSelector(
        state => state.kingdomProfilePageState
    )

    const [displayEmailInput, setDisplayEmailInput] = useState(
        !!values.studentEmail
    )

    const { data: studentProfileData } = useStudentProfileChoices()

    useEffect(() => {
        if (displayEmailInput || !studentProfileData?.schoolChoices) {
            return
        }

        setDisplayEmailInput(
            //$FlowFixMe
            studentProfileData.schoolChoices.find(
                schoolChoice => schoolChoice.schoolType === values.schoolType
            )?.requireEmail || false
        )
    }, [displayEmailInput, studentProfileData, values.schoolType])

    return (
        <>
            {studentProfileData?.schoolChoices && (
                <>
                    <Spacer />

                    <StyledSmallText>
                        {intl.formatMessage({
                            id:
                                'kingdom.profile.form.student.school.type.placeholder'
                        })}
                    </StyledSmallText>

                    <Spacer size={SIZE.XXS} />

                    <StyledDropdownContainer>
                        <DropdownSelect
                            margin="0"
                            width="100%"
                            color={
                                isProfileAlreadyValidated
                                    ? oldColors.veryDarkGrey
                                    : oldColors.text
                            }
                            background={
                                isProfileAlreadyValidated
                                    ? oldColors.chalk
                                    : 'transparent'
                            }
                            disabled={isProfileAlreadyValidated}
                            className={'schoolType'}
                            placeholderKey="kingdom.profile.form.student.school.type"
                            defaultValue={values.schoolType}
                            selectOptions={studentProfileData.schoolChoices.map(
                                schoolChoice => ({
                                    code: schoolChoice.schoolType,
                                    disabled: !schoolChoice.canBeSelected,
                                    label: intl.formatMessage({
                                        id: `kingdom.profile.form.student.school.type.${schoolChoice.schoolType}`
                                    })
                                })
                            )}
                            handleChange={value => {
                                setDisplayEmailInput(
                                    //$FlowFixMe
                                    studentProfileData.schoolChoices.find(
                                        schoolChoice =>
                                            schoolChoice.schoolType ===
                                            value.schoolType
                                    )?.requireEmail || false
                                )
                                setFieldValue('schoolType', value)
                                setFieldValue('studentEmail', undefined)
                            }}
                        />
                    </StyledDropdownContainer>
                </>
            )}

            {displayEmailInput && (
                <>
                    <Input
                        disabled={isProfileAlreadyValidated}
                        name="studentEmail"
                        labelKey="kingdom.profile.form.student.email"
                        placeholderKey="kingdom.profile.form.student.email"
                        className={
                            errors.studentEmail && touched.studentEmail
                                ? 'error'
                                : ''
                        }
                        value={values.studentEmail}
                    />

                    {errors.studentEmail && touched.studentEmail && (
                        <Error errorKey={errors.studentEmail} />
                    )}

                    <Spacer />

                    <StyledInfoContainer>
                        <Info color={COLOR.PRIMARY} style={{ flex: 'none' }} />

                        <Spacer
                            orientation={ORIENTATION.HORIZONTAL}
                            size={SIZE.XS}
                        />

                        <StyledInfoEmail>
                            {intl.formatMessage({
                                id: 'kingdom.profile.form.student.email.infos'
                            })}
                        </StyledInfoEmail>
                    </StyledInfoContainer>
                </>
            )}

            {!isProfileAlreadyValidated && values.schoolType && (
                <>
                    <Spacer />

                    {profileUpdateError && (
                        <Text color={oldColors.red}>
                            {intl.formatMessage({ id: profileUpdateError })}
                        </Text>
                    )}

                    <StyledButtonContainer>
                        <ButtonMedium
                            type="submit"
                            background={oldColors.yellow}
                            border={oldColors.yellow}
                            disabled={isSubmitting}>
                            {intl.formatMessage({
                                id: 'kingdom.profile.form.button.save'
                            })}
                        </ButtonMedium>
                    </StyledButtonContainer>
                </>
            )}
        </>
    )
}

const StyledSmallText = styled(SmallText)`
    color: ${oldColors.grey};
`

const StyledButtonContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
`
const StyledDropdownContainer = styled.div`
    width: 100%;
    border: ${BORDER.WIDTH.XS} solid ${colors.bbq30};
    border-radius: ${BORDER.RADIUS.L};
`

const StyledInfoContainer = styled.div`
    display: flex;
    flex-direction: row;
    width: 90%;
`

const StyledInfoEmail = styled(SmallText)`
    color: ${COLOR.PRIMARY};
`
